import { createServerFn } from '@tanstack/react-start';
import { z } from 'zod';

import { ApiParamsType } from '@/features/shared/types/ApiParamsType';
import { OneRosterApiTyped } from '@/lib/http.server';
import { ELEVATED_ROLES } from '@/lib/constants/auth.constants';
import { standardMiddlewares } from '@/middlewares/standardMiddlewares';
import { assertUserHasRole } from '@/middlewares/userAuthMiddleware';

import { User } from '../validators/schemas';

const getUsersDefaultParamsValues = {
  filter: `status='active'`,
  sort: 'givenName',
  orderBy: 'asc',
  limit: 100,
  offset: 0,
  search: '',
} as const;

export const getUsersDefaultParams =
  getUsersDefaultParamsValues satisfies ApiParamsType;

type UsersResponse = {
  users: User[];
  totalCount: number;
  pageCount: number;
  pageNumber: number;
  offset: number;
  limit: number;
};

type GetUsersResponse = {
  data: UsersResponse | null;
  success: boolean;
  error: string | null;
};

const fetchOnerosterUsers = async (
  onerosterApi: OneRosterApiTyped,
  params: ApiParamsType
) => {
  const response = await onerosterApi.GET(
    '/ims/oneroster/rostering/v1p2/users/',
    {
      params: {
        query: {
          ...getUsersDefaultParams,
          ...params,
        },
      },
    }
  );

  return response.data;
};

export const getUsers = createServerFn({ method: 'POST' })
  .middleware([standardMiddlewares])
  .validator(
    z.object({
      filter: z.string().optional().default(getUsersDefaultParams.filter),
      sort: z.string().optional().default(getUsersDefaultParams.sort),
      limit: z.number().optional().default(getUsersDefaultParams.limit),
      offset: z.number().optional().default(getUsersDefaultParams.offset),
      search: z.string().optional().default(getUsersDefaultParams.search),
    })
  )
  .handler(async ({ context: { oneRosterApiTyped, cognitoUser }, data }) => {
    await assertUserHasRole(cognitoUser, oneRosterApiTyped, ELEVATED_ROLES);
    const response = await fetchOnerosterUsers(oneRosterApiTyped, data);

    return {
      data: response,
      success: true,
      error: null,
    } as GetUsersResponse;
  });
